import { facadesApi, facadesUrl } from "@/store/services/facades";
import { PeaSummaryResponse } from "@/store/services/facades/types/pea";

export type PeaProgressRequest = string[];
export type PeaProgressResponse = {
  report_id: string;
  status: string;
  position?: number;
}[];

export const peaFacadesApi = facadesApi.injectEndpoints({
  endpoints: (build) => ({
    checkPortfolioProgress: build.query<PeaProgressResponse, PeaProgressRequest>({
      queryFn: async () => ({ data: [] }),
      async onCacheEntryAdded(reportIds, { updateCachedData, cacheEntryRemoved }) {
        const controller = new AbortController();

        try {
          const response = await fetch(`${facadesUrl}portfolio/progress`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${process.env.NEXT_PUBLIC_FACADES_ABCDE_API_KEY}`,
            },
            body: JSON.stringify({ report_ids: reportIds }),
            signal: controller.signal,
          });

          const reader = response.body?.getReader();
          const decoder = new TextDecoder();

          while (reader) {
            const { value, done } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });

            for (const line of chunk.split("\n").filter(Boolean)) {
              try {
                const parsed = JSON.parse(line);
                const entries = Array.isArray(parsed) ? parsed : [parsed];

                for (const data of entries) {
                  updateCachedData((draft) => {
                    const index = draft.findIndex((d) => d.report_id === data.report_id);
                    if (index > -1) {
                      const existing = draft[index];
                      const hasChanged = existing.status !== data.status || existing.position !== data.position;

                      if (hasChanged) {
                        draft[index] = { ...existing, ...data };
                      }
                    } else {
                      draft.push(data);
                    }
                  });
                }
              } catch (err) {
                console.error("Invalid JSON in stream:", line, err);
              }
            }
          }
        } catch (err) {
          console.error("Stream error:", err);
        }

        await cacheEntryRemoved;
        controller.abort();
      },
    }),
    getPortfolioSummaryResults: build.query<
      PeaSummaryResponse,
      { summaryId: string; settings?: { currency_code: string } }
    >({
      query: ({ summaryId, settings }) => ({
        url: "portfolio/results",
        method: "POST",
        headers: {
          Authorization: `Basic ${process.env.NEXT_PUBLIC_FACADES_ABCDE_API_KEY}`,
        },
        body: { report_id: summaryId, settings },
      }),
    }),
  }),
});

export const { useCheckPortfolioProgressQuery, useGetPortfolioSummaryResultsQuery } = peaFacadesApi;
